<template>
  <div class="mt-2">
    <Spinner v-if="isLoading" />
    <div v-else>
      <OperationsBreadcrumb :links="links" />
      <Message v-if="!!errorMessage.message" :message="errorMessage.message" :icon="errorMessage.icon" />
      <div v-else>
        <div class="columns">
          <div class="column is-three-quarters">
            <div class="card-item mt-5 mb-0">
              <div class="is-flex is-justify-content-space-between is-align-items-center">
                <h3 class="has-text-black">{{ `${client.name} ${client.lastName}` }}</h3>
                <span
                  class="is-flex is-align-items-center has-text-grey">
                  {{ $t(operation.isActive === true ? 'enabled': 'disabled') }}
                  <i
                    class="ml-1 fa-solid fa-circle"
                    :class="[operation.isActive === true ? 'has-text-primary' : 'has-text-danger']" />
                  <i v-if="!hasUnbalancedOperations && !hasUnconsistantBalance" class="ml-1 has-text-primary fa-regular fa-circle-check" />
                </span>
              </div>
              <div>
                <b-tag
                  v-if="operation.companyOperationId"
                  type="is-primary is-light"
                  icon="fa-solid fa-tag"
                  data-cy="resume-company-operation-id"
                  class="mr-4 mb-2"
                  rounded>
                  {{ operation.companyOperationId }}
                </b-tag>
                <b-tag
                  v-if="operation.type"
                  type="is-primary is-light"
                  :icon="iconType[operation.type]"
                  data-cy="resume-operation-type"
                  class="mr-4 mb-2"
                  rounded>
                  {{ operation.type }}
                </b-tag>
                <b-tag
                  v-if="operation.approvedAmount"
                  type="is-primary is-light"
                  icon="fa-solid fa-sack-dollar"
                  data-cy="resume-approved-amount"
                  class="mr-4 mb-2"
                  rounded>
                  {{ formatApprovedAmount(operation.approvedAmount) }}
                </b-tag>
                <b-tag
                  v-if="lastInvestor"
                  type="is-primary is-light"
                  icon="fa-solid fa-address-book"
                  data-cy="resume-investor"
                  class="mr-4 mb-2"
                  rounded>
                  {{ lastInvestor }}
                </b-tag>
              </div>
            </div>
          </div>
          <div class="column">
            <div class="card-item mt-5 mb-0 py-2 summary-card">
              <span class="is-flex is-align-items-center mb-1">
                <span class="icon-item is-flex is-align-items-center is-justify-content-center">
                  <i class="fa-solid fa-dollar-sign has-text-primary is-size-5" />
                </span>
                <span>
                  <h3 class="has-text-black is-size-6 mr-2">{{ $t('summary') }} </h3>
                </span>
              </span>
              <div class="is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start summary-tags">
                <b-tag
                  type="is-light"
                  icon="fa-solid fa-arrow-down-wide-short"
                  data-cy="resume-payment-stats"
                  class="mr-4 mb-2"
                  rounded>
                  Cuotas pagadas: {{ paymentsMade }}/{{ operation.termAsMonths }}
                  <b-tooltip
                    v-if="hasUnbalancedOperations || hasUnconsistantBalance"
                    type="is-light is-danger"
                    :label="`${hasUnbalancedOperations ? '(pagamentos desequilibrados) ' : ' '}${hasUnconsistantBalance ? '(saldos inconsistentes)' : ' '}`"
                    class="unbalanced-warning">
                    <i class="fa-solid fa-circle-exclamation is-size-7 has-text-danger" />
                  </b-tooltip>
                </b-tag>
                <iframe v-if="loadingState" src="https://lottie.host/embed/d3431eb9-579c-41a2-9647-7c7b5f0efb04/PO5xTewVFt.json" height="30" width="30" />
                <b-tag
                  v-else
                  type="is-light"
                  icon="fa-solid fa-dollar-sign"
                  data-cy="resume-payment-stats"
                  class="mr-4 mb-2"
                  rounded>
                  <span v-if="operation.remaining?.remainingBalance">{{ $t('debtAmountLabel') }}: {{ formatApprovedAmount(operation.remaining.remainingBalance) }}</span>
                  <span v-if="!operation.remaining?.remainingBalance">{{ $t('remainingBalanceNotAvailable') }}</span>
                </b-tag>
              </div>
            </div>
          </div>
        </div>
        <div data-cy="operation-content-buttons">
          <Tabs
            class="mt-0"
            :options="contentButtons">
            <template #1>
              <div
                id="operation-cap-management"
                class="card-item">
                <AmortizationSchedule
                  :is-loading-new-date="loadingState"
                  :operation="operation"
                  :date-for-amortization-schedule="dateForAmortizationSchedule"
                  :client="client"
                  :show-table="false"
                  @new-date="getOperation($event)" />
                <OperationModifier
                  class="mt-4"
                  :is-update-new-date="loadingState"
                  :operation="operation"
                  @cap-modification="refreshOperation" />
              </div>
            </template>
            <template #0>
              <div
                v-if="operation.isActive === false"
                data-cy="inactive-operation-reason-card"
                class="has-text-centered has-text-grey is-flex is-flex-direction-column is-justify-content-center is-align-items-center">
                <iframe class="animation-inactive" src="https://lottie.host/embed/69161ad5-fced-4882-9929-3558c12e0fad/1X6C7aQVOX.json" />
                <p class="is-size-5 has-text-weight-medium">{{ $t('inactiveOperationMessage') }}</p>
                <b-tag size="is-small" rounded type="is-light is-warning">{{ `${operationReasonForDisablingOrEnabling}` }}</b-tag>
                <p class="is-size-6 mt-2">
                  {{ $t('contactInformation') }} <a :href="'mailto:g_producto_transformation_team@creditu.com'">Transformation Team Producto</a>
                </p>
              </div>
              <AmortizationSchedule
                v-else
                :is-loading-new-date="loadingState"
                :operation="operation"
                :date-for-amortization-schedule="dateForAmortizationSchedule"
                :client="client"
                @new-date="getOperation($event)" />
            </template>
          </Tabs>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Message from '@/ui/components/Message.vue';
import Spinner from '@/ui/components/Spinner.vue';
import { getOperation } from '@/application/services/api-clients/operations';
import { getTheoreticalAmortisationSchedule } from '@/application/services/api-clients/amortization-schedule';
import { getClient } from '@/application/services/api-clients/clients';
import { getInvestors } from '@/application/services/api-clients/investors';
import { formatNumberByCountry } from '@/infrastructure/format';
import OperationModifier from '@/ui/components/internal/settings/OperationModifier.vue';
import AmortizationSchedule from '@/ui/components/internal/operations/AmortizationSchedule.vue';

import Tabs from '@/ui/components/Tabs.vue';
import notification from '@/ui/mixins/notification';
import OperationsBreadcrumb from '@/ui/views/internal/operations/OperationsBreadcrumb.vue';

export default {
  name: 'OperationManagement',
  components: { OperationsBreadcrumb, Message, Spinner, OperationModifier, AmortizationSchedule, Tabs },
  mixins: [notification],
  data() {
    return {
      client: null,
      clientId: null,
      currentPage: 1,
      dateForAmortizationSchedule: new Date(),
      errorMessage: { message: null, icon: null },
      iconType: {
        PARKING: 'fa-solid fa-square-parking',
        MORTGAGE: 'fa-solid fa-house',
        COMPLEMENTARY: 'fa-solid fa-plug-circle-plus',
        SME: 'fa-solid fa-store',
        CONSUME: 'fa-solid fa-bag-shopping',
        HOME_EQUITY: 'fa-solid fa-house-circle-check',
        DOWN_PAYMENT: 'fa-solid fa-hand-holding-dollar'
      },
      isLoading: true,
      loadingState: false,
      lastInvestor: null,
      operation: null,
      operationId: this.$route.params.operationId,
      paymentsMade: null,
      hasUnbalancedOperations: null,
      selectedTab: 'summary-tab',
      links: []
    };
  },
  computed: {
    contentButtons() {
      let contentButtons = [];
      if (this.operation) {
        contentButtons = [
          {
            label: this.$t('tableFull'),
            icon: '',
            show: ''
          },
          {
            label: 'Administrar',
            icon: '',
            show: this.$i18n.locale === 'br'
          }
        ];
      }
      return contentButtons;
    },
    operationReasonForDisablingOrEnabling() {
      const reason = this.operation.reasonForDisablingOrEnabling;
      if (reason) {
        return `${this.$t('reasonForDisabling')} ${reason}`;
      }
      return this.$t('noReasonForDisabling');
    }
  },
  async mounted() {
    try {
      this.onResize();
      window.addEventListener('resize', this.onResize, { passive: true });
      await this.getOperation(this.dateForAmortizationSchedule);
      await this.getClient();
      await this.getInvestors();
      this.setLinksForBreadcrumb();
    } catch {
      // Do nothing
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    onResize() {
      this.tabsSize = window.innerWidth < 500 ? 'is-small' : 'is-medium';
    },
    async getOperation(date) {
      this.loadingState = true;
      this.dateForAmortizationSchedule = date;
      try {
        if (this.$i18n.locale === 'br') {
          this.operation = await getTheoreticalAmortisationSchedule(this.operationId, date.toISOString().substring(0, 10));
        } else {
          this.operation = await getOperation(this.operationId);
        }
        const payments = this.operation.theoreticalPayments || this.operation.payments;
        this.paymentsMade = payments.reduce((acc, payment) => {
          return (payment.isPaid && payment.paymentNumber > acc) ? payment.paymentNumber : acc;
        }, 0);
        this.clientId = this.operation.clientId;
        this.hasUnbalancedOperations = payments.some(theoreticalPayment => theoreticalPayment.isBalanced === false);
        this.hasUnconsistantBalance = payments.some(theoreticalPayment => theoreticalPayment.isBalanceConsistant === false);
      } catch (_error) {
        this.errorMessage = {
          message: 'Ocurrió un error al obtener la operación',
          icon: 'data_maintenance.svg'
        };
        throw _error; // Para interrumpir mounted
      }
      this.loadingState = false;
    },
    async getClient() {
      try {
        this.client = await getClient(this.clientId);
      } catch (_error) {
        this.errorMessage = {
          message: 'Ocurrió un error al obtener la información del cliente',
          icon: 'data_maintenance.svg'
        };
        throw _error; // Para interrumpir mounted
      }
    },
    async getInvestors() {
      try {
        const investors = await getInvestors();
        this.lastInvestor = investors
          .find(investor => investor._id === this.operation.payments[this.operation.payments.length - 1]?.investorId)?.name;
      } catch (_error) {
        this.errorMessage = {
          message: 'Ocurrió un error al obtener la información de los inversionistas',
          icon: 'data_maintenance.svg'
        };
        throw _error; // Para interrumpir mounted
      }
    },
    formatApprovedAmount(number) {
      const prefix = {
        br: 'R$ ',
        cl: 'UF ',
        pe: 'S/ '
      }[this.$i18n.locale];
      const formattedNumber = this.formatCurrency(number);
      return `${prefix}${formattedNumber}`;
    },
    formatCurrency(number) {
      const fractionDigits = {
        cl: ['MORTGAGE', 'PARKING'].includes(this.operation.type) ? 6 : 0,
        br: 2,
        pe: 2
      }[this.$i18n.locale];

      return formatNumberByCountry({
        number,
        countryIn: this.$i18n.locale,
        usePrefix: false,
        fractionDigits
      });
    },
    async refreshOperation() {
      this.isLoading = true;
      try {
        const today = new Date();
        await this.getOperation(today);
      } catch {
        this.operation = null;
        // Do nothing
      } finally {
        this.isLoading = false;
      }
    },
    setLinksForBreadcrumb() {
      const fullName = `${this.client.name} ${this.client.lastName}`;
      this.links = [
        { label: fullName, to: { name: 'operations' }, active: true }
      ];
    }
  }
};
</script>

<style scoped>
  .grand-size-text {
    font-size: 1.5rem;
  }
  .big-size-text {
    font-size: 1.2rem;
  }
  .med-size-text {
    font-size: 0.9rem;
  }
  .operation-management-header {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0rem 1rem;
    place-items: center;
    color: var(--primary);
    font-weight: 600;
  }
  .operation-management-header .header-name {
    justify-self: flex-start;
  }
  .operation-management-header .button {
    justify-self: flex-end;
  }
  .card-item.operation-info {
    z-index: 0; /* Para que el datepicker del eliminar gastos de cobranza pase por arriba del CollectionHistory */
  }
  .card-item {
    width: auto;
    margin: 0 0 2rem 0;
    padding: 1% 1.5rem 2rem;
    overflow: visible !important;
  }
  .operation-content-buttons {
    margin: 1rem;
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: space-evenly;
    gap: 1rem;
  }
  .operation-content-buttons .selected-content-button {
    background-color: var(--primary);
    color:white;
  }

  .animation-inactive {
    opacity: 0.5;
  }

  .summary-card {
      min-height: 126px;
    }

  .summary-card .icon-item {
    zoom: 0.7;
  }

  .summary-tags {
    min-height: 58px;
  }

  .spinner-date {
    max-height: 25px;
    overflow: hidden;
  }

  .unbalanced-warning {
    bottom: 8px;
    right: -14px;
    margin-left: -10px;
  }
  @media (max-width: 768px) {
    .summary-card {
      margin-top: 0px !important;
    }
  }
  @media (max-width: 350px) {
    .grand-size-text {font-size: 1.2rem;}
    .big-size-text {font-size: 0.9rem;}
    .med-size-text {font-size: 0.75rem;}
  }
  .tooltip {
    pointer-events: none;
  }
  .tag.paid {
  background-color: var(--primary);
  }

  .tag.unpaid {
    background-color: silver;
  }
  /* Sets grace month text color to red */
  p.is-grace-month {
    color: silver;
  }
</style>
<style>
@media screen and (min-width: 769px) {
  #operations .table-wrapper{
    overflow: hidden;
  }
}
</style>
<i18n>
  {
    "cl": {
      "approvedAmount": "Monto del Crédito",
      "backButton": "Volver",
      "perPage": "por página",
      "tableFull":"Tabla",
      "summary": "Resumen",
      "enabled": "Activa",
      "disabled": "Inactiva",
      "debtAmountLabel": "Saldo insoluto",
      "latePaymentsLabel": "Cuotas atrasadas",
      "noReasonForDisabling": "Sin motivo indicado",
      "reasonForDisabling": "Motivo del estado inactivo:",
      "contactInformation": "Para mayor detalles de la operación, contactar a",
      "inactiveOperationMessage": "Esta operación se encuentra inactiva",
      "remainingBalanceNotAvailable": "Saldo insoluto no disponible"
    },
    "pe": {
      "approvedAmount": "Monto del Crédito",
      "backButton": "Volver",
      "perPage": "por página",
      "summary": "Resumen",
      "enabled": "Activa",
      "disabled": "Inactiva",
      "debtAmountLabel": "Saldo insoluto",
      "latePaymentsLabel": "Cuotas atrasadas",
      "noReasonForDisabling": "Sin motivo indicado",
      "reasonForDisabling": "Motivo del estado inactivo:",
      "contactInformation": "Para mayor detalles de la operación, contactar a",
      "inactiveOperationMessage": "Esta operación se encuentra inactiva",
      "remainingBalanceNotAvailable": "Saldo insoluto no disponible"
    },
    "br": {
      "approvedAmount": "Monto do Crédito",
      "backButton": "Voltar",
      "perPage": "por página",
      "tableFull":"Tabela",
      "summary": "Resumo",
      "enabled": "Ativa",
      "disabled": "Inativa",
      "debtAmountLabel": "Saldo devedor",
      "latePaymentsLabel": "Parcelas atrasadas",
      "noIpcaWarning": "Valores não incluem IPCA e estão em Reais",
      "inflation": "Inflação",
      "tac": "TAC",
      "TACValue": "TAC",
      "lifeInsurance": "MIP",
      "propertyInsurance": "DFI",
      "creditInsurancePrizeOtro": "Seguro de crédito (Prêmio)",
      "creditInsurancePrize": "SCP",
      "creditInsuranceExpenses": "SCE",
      "totalInsurances": "Total seguros",
      "noReasonForDisabling": "Nenhum motivo de status indicado",
      "reasonForDisabling": "Motivo do Status inativo:",
      "contactInformation": "Para maiores detalhes da operação, entre em contato com",
      "inactiveOperationMessage": "Esta operação está inativa",
      "remainingBalanceNotAvailable": "Saldo devedor não disponível"
    }
  }
</i18n>
